import { ShoppingBag, Trash2, Sparkles, ArrowRight, CheckCircle, Lock, Phone, Mail } from "lucide-react";
import { motion } from "framer-motion";

const OrderSummary = ({
  cartItems = [],
  bikeData = {},
  subtotal = 0,
  doorstepCharge = 0,
  gst = 0,
  total = 0,
  handleRemoveItem,
  currentStep,
  isLoggedIn,
  step1Valid,
  handleContinueStep1,
  handleContinueStep2,
}) => {
  const totalSavings = Array.isArray(cartItems)
    ? cartItems.reduce((sum, item) => sum + ((item.price || 0) - (item.discountedPrice || 0)), 0)
    : 0;

  const getButtonConfig = () => {
    if (currentStep === 1) {
      return {
        show: true,
        text: "Continue to Account",
        onClick: handleContinueStep1,
        disabled: !step1Valid || !isLoggedIn,
        disabledMessage: !isLoggedIn ? "Please login first" : "Complete all required fields",
      };
    } else if (currentStep === 2) {
      return {
        show: true,
        text: "Continue to Review",
        onClick: handleContinueStep2,
        disabled: false,
      };
    }
    return { show: false };
  };

  const buttonConfig = getButtonConfig();

  return (
    <div className="lg:sticky lg:top-24 space-y-4">
      {/* Main Order Card */}
      <div className="bg-[#111111] border border-white/[0.08] rounded-2xl overflow-hidden">
        {/* Header */}
        <div className="bg-[#FF6B35]/10 border-b border-white/[0.08] p-5 flex items-center gap-2">
          <ShoppingBag className="h-5 w-5 text-[#FF6B35]" />
          <h2 className="text-base font-bold text-white">Order Summary</h2>
        </div>

        <div className="p-5 space-y-5">
          {/* Vehicle */}
          <div className="pb-4 border-b border-white/[0.06]">
            <p className="text-[10px] font-bold text-gray-500 uppercase tracking-widest mb-3">Your Vehicle</p>
            <div className="flex items-center gap-3 p-3 bg-white/[0.04] border border-white/[0.06] rounded-xl">
              {bikeData.image && (
                <img
                  src={bikeData.image}
                  alt="Bike"
                  className="w-14 h-14 object-cover rounded-lg flex-shrink-0"
                  onError={(e) => { e.target.style.display = "none"; }}
                />
              )}
              <div className="min-w-0">
                <p className="font-bold text-white text-sm line-clamp-1">
                  {bikeData.manufacturer || "Unknown"} {bikeData.model || "Model"}
                </p>
                {bikeData.fuelType && (
                  <p className="text-xs text-gray-500 mt-0.5">{bikeData.fuelType}</p>
                )}
              </div>
            </div>
          </div>

          {/* Services */}
          <div className="pb-4 border-b border-white/[0.06]">
            <p className="text-[10px] font-bold text-gray-500 uppercase tracking-widest mb-3">
              Selected Services ({cartItems.length})
            </p>
            {cartItems.length > 0 ? (
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {cartItems.map((item) => (
                  <div key={item.id} className="group flex items-start gap-3 p-3 bg-white/[0.03] border border-white/[0.05] rounded-xl hover:border-white/10 transition-colors">
                    {item.imageUrl && (
                      <img
                        src={item.imageUrl}
                        alt={item.name}
                        className="w-12 h-12 object-cover rounded-lg flex-shrink-0"
                        onError={(e) => { e.target.style.display = "none"; }}
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-white text-sm line-clamp-2 leading-snug">{item.name || "Service"}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="font-bold text-[#FF6B35] text-sm">₹{item.discountedPrice || 0}</span>
                        {item.price > item.discountedPrice && (
                          <span className="text-xs text-gray-600 line-through">₹{item.price}</span>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => handleRemoveItem && handleRemoveItem(item.id)}
                      className="p-1.5 text-gray-600 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-all flex-shrink-0"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-6">
                <ShoppingBag className="h-10 w-10 text-gray-700 mx-auto mb-2" />
                <p className="text-sm text-gray-600">No services added</p>
              </div>
            )}
          </div>

          {/* Pricing */}
          <div className="space-y-2.5">
            <div className="flex justify-between items-center text-sm">
              <span className="text-gray-400">Subtotal</span>
              <span className="text-white font-semibold">₹{subtotal.toFixed(2)}</span>
            </div>
            <div className="flex justify-between items-center text-sm">
              <span className="text-gray-400">GST (5%)</span>
              <span className="text-white font-semibold">₹{gst.toFixed(2)}</span>
            </div>
            {doorstepCharge > 0 && (
              <div className="flex justify-between items-center text-sm">
                <span className="text-gray-400">Doorstep Charge</span>
                <span className="text-white font-semibold">₹{doorstepCharge.toFixed(2)}</span>
              </div>
            )}
            <div className="pt-3 border-t border-white/[0.08] flex justify-between items-center">
              <span className="text-white font-bold">Total Amount</span>
              <span className="text-xl font-black text-[#FF6B35]">₹{total.toFixed(2)}</span>
            </div>
          </div>

          {totalSavings > 0 && (
            <div className="flex items-center justify-center gap-2 p-3 bg-green-500/10 border border-green-500/20 rounded-xl">
              <Sparkles className="h-4 w-4 text-green-400 flex-shrink-0" />
              <span className="text-sm font-bold text-green-400">You're saving ₹{totalSavings.toFixed(2)}!</span>
            </div>
          )}
        </div>
      </div>

      {/* Continue Button */}
      {buttonConfig.show && (
        <div className="bg-[#111111] border border-white/[0.08] rounded-2xl overflow-hidden">
          <motion.button
            whileTap={{ scale: 0.98 }}
            onClick={buttonConfig.onClick}
            disabled={buttonConfig.disabled}
            className={`w-full p-4 text-white font-bold text-sm flex items-center justify-center gap-2 group transition-colors ${
              buttonConfig.disabled
                ? "bg-white/[0.04] text-gray-600 cursor-not-allowed"
                : "bg-[#FF6B35] hover:bg-[#E85520]"
            }`}
          >
            {buttonConfig.disabled ? (
              <>
                <Lock className="h-4 w-4" />
                <span>{buttonConfig.disabledMessage}</span>
              </>
            ) : (
              <>
                <span>{buttonConfig.text}</span>
                <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </>
            )}
          </motion.button>
          <div className="px-4 py-3 border-t border-white/[0.06] flex items-center justify-center gap-4 text-xs text-gray-600">
            <div className="flex items-center gap-1">
              <CheckCircle className="h-3.5 w-3.5 text-[#FF6B35]" />
              <span>Secure checkout</span>
            </div>
            <div className="flex items-center gap-1">
              <CheckCircle className="h-3.5 w-3.5 text-[#FF6B35]" />
              <span>Free cancellation</span>
            </div>
          </div>
        </div>
      )}

      {/* Help */}
      <div className="bg-[#111111] border border-white/[0.08] rounded-2xl p-4">
        <p className="text-[10px] font-bold text-gray-500 uppercase tracking-widest mb-3">Need Assistance?</p>
        <div className="grid grid-cols-2 gap-2">
          <a
            href="tel:+911234567890"
            className="flex items-center justify-center gap-1.5 px-3 py-2.5 bg-white/[0.04] border border-white/[0.06] text-gray-400 hover:text-[#FF6B35] hover:border-[#FF6B35]/20 rounded-xl transition-all text-xs font-semibold"
          >
            <Phone className="h-4 w-4" />
            Call Us
          </a>
          <a
            href="mailto:support@okmechanic.com"
            className="flex items-center justify-center gap-1.5 px-3 py-2.5 bg-white/[0.04] border border-white/[0.06] text-gray-400 hover:text-[#FF6B35] hover:border-[#FF6B35]/20 rounded-xl transition-all text-xs font-semibold"
          >
            <Mail className="h-4 w-4" />
            Email
          </a>
        </div>
      </div>
    </div>
  );
};

export default OrderSummary;
